const formatCurrency = (value, currency = "sgd") => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-SG", { style: "currency", currency: String(currency || "sgd").toUpperCase(), maximumFractionDigits: 2 }).format(amount);
};

const toneByEnvironment = {
  LIVE_PRODUCTION: "border-emerald-200 bg-emerald-50 text-emerald-700",
  TEST_MODE: "border-amber-200 bg-amber-50 text-amber-800",
};

export const AdminBillingTelemetryPanel = ({ overview = {}, transactions = [] }) => {
  return (
    <div className="rounded-[32px] border border-[#001935]/10 bg-white p-6 shadow-[0_24px_70px_rgba(0,25,53,0.08)]" data-testid="admin-billing-telemetry-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Billing Telemetry</p>
          <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-[#001935]">Mission Control payment mode ledger.</h2>
        </div>
        <div className="rounded-full border border-[#001935]/10 bg-[#f8fafc] px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#42566f]" data-testid="admin-billing-telemetry-count-pill">
          {overview.total_transactions || 0} tracked payments
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <div className="rounded-[22px] border border-[#001935]/10 bg-[#fffaf1] px-5 py-4" data-testid="billing-telemetry-total-card">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Total payments</p>
          <p className="mt-3 text-3xl font-semibold text-[#001935]">{overview.total_transactions || 0}</p>
        </div>
        <div className="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4" data-testid="billing-telemetry-test-mode-card">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-800">TEST_MODE</p>
          <p className="mt-3 text-3xl font-semibold text-[#001935]">{overview.test_mode_transactions || 0}</p>
        </div>
        <div className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-5 py-4" data-testid="billing-telemetry-live-production-card">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700">LIVE_PRODUCTION</p>
          <p className="mt-3 text-3xl font-semibold text-[#001935]">{overview.live_production_transactions || 0}</p>
        </div>
      </div>

      <div className="mt-6 hidden overflow-x-auto rounded-[24px] border border-[#001935]/8 md:block">
        <table className="min-w-full divide-y divide-[#001935]/8 text-left text-sm" data-testid="billing-telemetry-table">
          <thead className="bg-[#f8fafc] text-xs font-bold uppercase tracking-[0.18em] text-[#5e6e82]">
            <tr>
              <th className="px-4 py-3">Environment</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Payment Status</th>
              <th className="px-4 py-3">Checkout Mode</th>
              <th className="px-4 py-3">Referral</th>
              <th className="px-4 py-3">Created At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#001935]/8 text-[#31455f]">
            {transactions.map((transaction) => (
              <tr data-testid={`billing-telemetry-row-${transaction.session_id}`} key={transaction.session_id}>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] ${toneByEnvironment[transaction.transaction_environment] || toneByEnvironment.TEST_MODE}`} data-testid={`billing-telemetry-environment-${transaction.session_id}`}>
                    {transaction.transaction_environment}
                  </span>
                </td>
                <td className="px-4 py-3 font-semibold text-[#001935]">{transaction.user_email || "Public checkout"}</td>
                <td className="px-4 py-3">{transaction.plan_type || "—"}</td>
                <td className="px-4 py-3">{formatCurrency(transaction.amount, transaction.currency)}</td>
                <td className="px-4 py-3">{transaction.payment_status || "—"}</td>
                <td className="px-4 py-3 text-xs uppercase tracking-[0.14em] text-[#5e6e82]">{transaction.checkout_mode || "—"}</td>
                <td className="px-4 py-3">{transaction.referral_partner_code || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#5e6e82]">{transaction.created_at || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid gap-4 md:hidden" data-testid="billing-telemetry-mobile-list">
        {transactions.map((transaction) => (
          <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5 shadow-[0_16px_45px_rgba(0,25,53,0.06)]" data-testid={`billing-telemetry-mobile-card-${transaction.session_id}`} key={`mobile-${transaction.session_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-lg font-semibold text-[#001935]">{transaction.user_email || "Public checkout"}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#8b6d16]">{transaction.plan_type || "—"}</p>
              </div>
              <span className={`rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] ${toneByEnvironment[transaction.transaction_environment] || toneByEnvironment.TEST_MODE}`} data-testid={`billing-telemetry-mobile-environment-${transaction.session_id}`}>
                {transaction.transaction_environment}
              </span>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Amount</p>
                <p className="mt-2 text-sm font-semibold text-[#001935]">{formatCurrency(transaction.amount, transaction.currency)}</p>
              </div>
              <div className="rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Payment status</p>
                <p className="mt-2 text-sm font-semibold text-[#001935]">{transaction.payment_status || "—"}</p>
              </div>
              <div className="rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Checkout mode</p>
                <p className="mt-2 text-sm font-semibold text-[#001935]">{transaction.checkout_mode || "—"}</p>
              </div>
              <div className="rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Referral</p>
                <p className="mt-2 text-sm font-semibold text-[#001935]">{transaction.referral_partner_code || "—"}</p>
              </div>
            </div>
            {transaction.simulation_error ? (
              <div className="mt-4 rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid={`billing-telemetry-mobile-note-${transaction.session_id}`}>
                Safety-net note: {transaction.simulation_error}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};